import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Sprout, Mail, Lock, Loader2 } from 'lucide-react'
// eslint-disable-next-line @typescript-eslint/no-deprecated
import { Github as GitHubIcon } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

export default function SignIn() {
  const { signIn, signInWithGitHub } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const { error } = await signIn(email, password)
    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      navigate('/gardens/main')
    }
  }

  return (
    <div className="min-h-screen garden-bg relative flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--accent-garden)]/10 border border-[var(--accent-garden)]/20 mb-4 glow-garden">
            <Sprout className="w-8 h-8 text-[var(--accent-garden)]" />
          </div>
          <h1 className="heading-2 mb-2">Welcome Back</h1>
          <p className="text-small">Return to the garden</p>
        </div>

        {/* Sign In Card */}
        <div className="glass-panel rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm text-[var(--text-secondary)]">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm text-[var(--text-secondary)]">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center space-x-2 rounded-xl"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span>Enter the Garden</span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border-subtle)]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-transparent text-[var(--text-muted)]">or continue with</span>
            </div>
          </div>

          {/* GitHub OAuth */}
          <button
            onClick={signInWithGitHub}
            className="w-full btn-secondary py-3 flex items-center justify-center space-x-2 rounded-xl"
          >
            <GitHubIcon className="w-5 h-5" />
            <span>GitHub</span>
          </button>

          {/* Sign Up Link */}
          <p className="text-center text-small mt-6">
            New to the garden?{' '}
            <Link to="/signup" className="text-[var(--accent-soul)] hover:text-[var(--accent-soul)]/80 transition-colors">
              Request membership
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-[var(--text-muted)] text-xs mt-8 italic">
          "Invitation over evaluation"
        </p>
      </div>
    </div>
  )
}
